import React, { useState, useEffect } from 'react';
import useAppwrite from '../../../lib/useAppwrite';
import { config, deleteDocument, getAllDocuments, getCurrentUser } from '../../../lib/appwrite';
import { useStateContext } from '../../../contexts/ContextProvider';
import { FaCirclePlus, FaTrashCan } from "react-icons/fa6";
import { FaRegEdit } from "react-icons/fa";
import EditPhoto from './EditPhoto';
import ConfirmDelete from '../../../components/ConfirmDelete';

const Photos = () => {
  const { data: user } = useAppwrite(() => getCurrentUser());
  const { currentColor } = useStateContext();
  const [createRecord, setCreateRecord] = useState(false);
  const [editRecord, setEditRecord] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [photos, setPhotos] = useState([]);

  // Data
  const { data, isLoading, refetch } = useAppwrite(
    () => getAllDocuments(config.photosCollectionId)
  );

  useEffect(() => {
    if (data) {
      setPhotos(data);
    }
  }, [data]);

  useEffect(() => {
    if (editRecord === false && createRecord === false) {
      refetch();
    }
  }, [editRecord, createRecord]);

  const handleRowSelect = (photo) => {
    setSelectedRowData(photo);
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    await deleteDocument(selectedRowData.$id || selectedRowData.id, config.photosCollectionId);
    refetch();
    setSelectedRowData(null);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Photos</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your photo gallery
            </p>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedRowData(null);
              setCreateRecord(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
          >
            <FaCirclePlus />
            <span>Upload Photo</span>
          </button>
        </div>
      </div>

      {/* Photos Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading photos...</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No photos found.</p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedRowData(null);
                setCreateRecord(true);
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Upload your first photo
            </button>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {photos.map((photo) => (
                <div
                  key={photo.id || photo.$id}
                  className={`relative group bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedRowData?.id === photo.id || selectedRowData?.$id === photo.$id
                      ? 'ring-2 ring-blue-500'
                      : 'hover:shadow-lg'
                  }`}
                  onClick={() => handleRowSelect(photo)}
                >
                  {/* Photo */}
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={photo.photoUrl || '/placeholder-image.png'}
                      alt={photo.name || 'Photo'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.png';
                      }}
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(photo);
                            setEditRecord(true);
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Edit"
                        >
                          <FaRegEdit className="text-blue-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(photo);
                            setConfirmDelete(true);
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                          title="Delete"
                        >
                          <FaTrashCan className="text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Photo Name */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {photo.name || 'Untitled'}
                    </p>
                    {photo.created_at && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(photo.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {createRecord && (
        <EditPhoto
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={null}
          setSelectedRowData={setSelectedRowData}
          mode="create"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}
      {editRecord && (
        <EditPhoto
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          mode="edit"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDelete
        title="Confirm Deletion?"
        text="Are you sure you want to delete this photo? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        visible={confirmDelete}
        setVisible={setConfirmDelete}
        handleConfirm={handleDelete}
      />
    </div>
  );
};

export default Photos;
